/**
 * Stage 73.20r — Employee-Grouped KYC Correction Requests workspace.
 *
 * Groups KYC correction requests by employee into a clean top-level list.
 * Clicking "View Requests" opens an employee detail drawer showing all field
 * requests submitted by that employee with request-level Approve & Reject actions.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import MainLayout from '../components/MainLayout';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import {
  Check, X, ChevronLeft, Loader2, RefreshCw, Download, FileText,
  AlertTriangle, Search, Users, Clock, CheckCircle2, XCircle, ArrowRight
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useActiveCompany } from '../hooks/useActiveCompany';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const STATUS_COLOR = {
  pending: 'bg-amber-100 text-amber-800 border-amber-200',
  approved: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  rejected: 'bg-rose-100 text-rose-800 border-rose-200',
};

export default function KYCCorrections() {
  const [selectedCompany] = useActiveCompany();
  const [summary, setSummary] = useState({
    total_employees: 0,
    pending_employees: 0,
    total_requests: 0,
    pending_requests: 0,
    approved_requests: 0,
    rejected_requests: 0,
  });
  const [employees, setEmployees] = useState([]);
  const [filter, setFilter] = useState('pending');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Right-side detail drawer state
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);

  // Decision Modal state (Approve or Reject a single request)
  const [decisionFor, setDecisionFor] = useState(null); // { item, action: 'approve' | 'reject', employeeName }
  const [remarks, setRemarks] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    if (!selectedCompany) return;
    setLoading(true);
    try {
      const qs = new URLSearchParams({ company_id: selectedCompany });
      if (filter && filter !== 'all') qs.set('status', filter);
      if (search.trim()) qs.set('search', search.trim());

      const res = await axios.get(`${API}/kyc/corrections/grouped?${qs.toString()}`, { withCredentials: true });
      const data = res.data || {};
      setSummary(data.summary || {
        total_employees: 0,
        pending_employees: 0,
        total_requests: 0,
        pending_requests: 0,
        approved_requests: 0,
        rejected_requests: 0,
      });
      setEmployees(data.employees || []);
    } catch (e) {
      toast.error('Load failed: ' + (e?.response?.data?.detail || e.message));
    }
    setLoading(false);
  }, [filter, search, selectedCompany]);

  useEffect(() => { load(); }, [load]);

  const decide = async () => {
    if (!decisionFor) return;
    if (decisionFor.action === 'reject' && !remarks.trim()) {
      toast.error('Reason for rejection is required');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/kyc/corrections/${decisionFor.item.id}/${decisionFor.action}`,
        { hr_remarks: remarks.trim() },
        { withCredentials: true },
      );
      toast.success(
        `Correction ${decisionFor.action}d` +
        (decisionFor.action === 'approve' ? ' — Employee master updated.' : '.')
      );
      setDecisionFor(null);
      setRemarks('');
      await load();
    } catch (e) {
      toast.error('Failed: ' + (e?.response?.data?.detail || e.message));
    }
    setSubmitting(false);
  };

  const downloadCSV = async () => {
    try {
      const res = await axios.get(`${API}/kyc/reports/corrections.csv`, { withCredentials: true, responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'kyc_corrections.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Download failed: ' + (e?.response?.data?.detail || e.message));
    }
  };

  // Currently active employee object for the right-side drawer
  const activeEmployee = selectedEmployeeId
    ? employees.find(e => e.employee_id === selectedEmployeeId)
    : null;

  return (
    <MainLayout>
      <div className="space-y-6 p-6" data-testid="kyc-corrections-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/payroll/kyc-cycles" className="text-slate-500 hover:text-blue-700 transition-colors">
              <ChevronLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">KYC Correction Requests</h1>
              <p className="text-sm text-slate-600 mt-0.5">
                Review employee-submitted corrections before they patch the master record.
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={downloadCSV} data-testid="kyc-corr-csv">
              <Download className="w-4 h-4 mr-1.5" /> Export CSV
            </Button>
            <Button variant="outline" size="sm" onClick={load} disabled={loading}>
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            </Button>
          </div>
        </div>

        {/* 1. Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="kyc-summary-cards">
          <Card className="p-4 bg-white border border-slate-200">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-blue-50 text-blue-700 rounded-lg">
                <Users className="w-5 h-5" />
              </div>
              <div>
                <p className="text-xs font-medium text-slate-500">Total Employees</p>
                <p className="text-xl font-bold text-slate-900">{summary.total_employees}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 bg-white border border-slate-200">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-amber-50 text-amber-700 rounded-lg">
                <Clock className="w-5 h-5" />
              </div>
              <div>
                <p className="text-xs font-medium text-slate-500">Employees with Pending</p>
                <p className="text-xl font-bold text-amber-700">{summary.pending_employees}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 bg-white border border-slate-200">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-amber-50 text-amber-700 rounded-lg">
                <FileText className="w-5 h-5" />
              </div>
              <div>
                <p className="text-xs font-medium text-slate-500">Total Pending Requests</p>
                <p className="text-xl font-bold text-amber-700">{summary.pending_requests}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 bg-white border border-slate-200">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-emerald-50 text-emerald-700 rounded-lg">
                <CheckCircle2 className="w-5 h-5" />
              </div>
              <div>
                <p className="text-xs font-medium text-slate-500">Total Approved</p>
                <p className="text-xl font-bold text-emerald-700">{summary.approved_requests}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 bg-white border border-slate-200">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-rose-50 text-rose-700 rounded-lg">
                <XCircle className="w-5 h-5" />
              </div>
              <div>
                <p className="text-xs font-medium text-slate-500">Total Rejected</p>
                <p className="text-xl font-bold text-rose-700">{summary.rejected_requests}</p>
              </div>
            </div>
          </Card>
        </div>

        {/* 2. Filter & Search Controls */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white p-3 rounded-xl border border-slate-200">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-semibold text-slate-500 mr-1">Status Filter:</span>
            {[
              { id: 'pending', label: 'Pending Requests' },
              { id: 'approved', label: 'Approved Requests' },
              { id: 'rejected', label: 'Rejected Requests' },
              { id: 'all', label: 'All Employees' },
            ].map(s => (
              <button
                key={s.id}
                onClick={() => setFilter(s.id)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors border ${
                  filter === s.id
                    ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
                    : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
                }`}
                data-testid={`kyc-corr-filter-${s.id}`}
              >
                {s.label}
              </button>
            ))}
          </div>

          <div className="relative w-full sm:w-72">
            <Search className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
            <Input
              type="text"
              placeholder="Search Name, ID, Dept..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-9 text-xs h-9"
              data-testid="kyc-corr-search"
            />
          </div>
        </div>

        {/* 3. Grouped Employee Table */}
        <Card className="p-0 overflow-hidden border border-slate-200">
          {employees.length === 0 ? (
            <div className="text-center py-16 px-4">
              <Users className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-sm font-medium text-slate-700">No employee KYC correction requests found</p>
              <p className="text-xs text-slate-500 mt-1">
                {loading ? 'Loading requests...' : 'Try adjusting your search or status filter.'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left p-3 font-semibold text-slate-700">Employee</th>
                    <th className="text-left p-3 font-semibold text-slate-700">ID</th>
                    <th className="text-left p-3 font-semibold text-slate-700">Department / Designation</th>
                    <th className="text-center p-3 font-semibold text-slate-700">Total Requests</th>
                    <th className="text-center p-3 font-semibold text-slate-700">Pending</th>
                    <th className="text-center p-3 font-semibold text-slate-700">Approved</th>
                    <th className="text-center p-3 font-semibold text-slate-700">Rejected</th>
                    <th className="text-left p-3 font-semibold text-slate-700">Latest Submission</th>
                    <th className="p-3 text-right font-semibold text-slate-700">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100" data-testid="kyc-corr-table">
                  {employees.map(emp => {
                    const isSelected = selectedEmployeeId === emp.employee_id;
                    const initials = (emp.employee_name || 'E')
                      .split(' ')
                      .map(n => n[0])
                      .join('')
                      .toUpperCase()
                      .slice(0, 2);

                    return (
                      <tr
                        key={emp.employee_id}
                        className={`hover:bg-slate-50/80 transition-colors ${
                          isSelected ? 'bg-blue-50/50' : ''
                        }`}
                        data-testid={`kyc-emp-row-${emp.employee_id}`}
                      >
                        <td className="p-3">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 font-bold flex items-center justify-center text-xs shrink-0">
                              {initials}
                            </div>
                            <div>
                              <div className="font-semibold text-slate-900 text-xs">
                                {emp.employee_name}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="p-3 font-mono font-medium text-slate-600">
                          {emp.employee_code || '—'}
                        </td>
                        <td className="p-3">
                          <div className="font-medium text-slate-800">{emp.department || '—'}</div>
                          <div className="text-[10px] text-slate-500">{emp.designation || '—'}</div>
                        </td>
                        <td className="p-3 text-center">
                          <span className="px-2 py-0.5 bg-slate-100 text-slate-800 rounded font-semibold text-xs">
                            {emp.summary.total}
                          </span>
                        </td>
                        <td className="p-3 text-center">
                          <span
                            className={`px-2 py-0.5 rounded font-semibold text-xs ${
                              emp.summary.pending > 0
                                ? 'bg-amber-100 text-amber-800'
                                : 'bg-slate-100 text-slate-400'
                            }`}
                          >
                            {emp.summary.pending}
                          </span>
                        </td>
                        <td className="p-3 text-center">
                          <span
                            className={`px-2 py-0.5 rounded font-semibold text-xs ${
                              emp.summary.approved > 0
                                ? 'bg-emerald-100 text-emerald-800'
                                : 'bg-slate-100 text-slate-400'
                            }`}
                          >
                            {emp.summary.approved}
                          </span>
                        </td>
                        <td className="p-3 text-center">
                          <span
                            className={`px-2 py-0.5 rounded font-semibold text-xs ${
                              emp.summary.rejected > 0
                                ? 'bg-rose-100 text-rose-800'
                                : 'bg-slate-100 text-slate-400'
                            }`}
                          >
                            {emp.summary.rejected}
                          </span>
                        </td>
                        <td className="p-3 text-slate-500 text-xs">
                          {emp.latest_submitted_at ? emp.latest_submitted_at.slice(0, 10) : '—'}
                        </td>
                        <td className="p-3 text-right">
                          <Button
                            size="sm"
                            onClick={() => setSelectedEmployeeId(emp.employee_id)}
                            className="bg-blue-600 hover:bg-blue-700 text-white text-xs h-8 px-3"
                            data-testid={`kyc-view-emp-${emp.employee_id}`}
                          >
                            View Requests <ArrowRight className="w-3 h-3 ml-1" />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {/* 4. Right-Side Employee Detail Drawer */}
        {activeEmployee && (
          <div className="fixed inset-0 z-50 overflow-hidden" data-testid="kyc-emp-drawer">
            <div
              className="absolute inset-0 bg-black/40 backdrop-blur-xs transition-opacity"
              onClick={() => setSelectedEmployeeId(null)}
            />
            <div className="fixed inset-y-0 right-0 max-w-full flex pl-10">
              <div className="w-screen max-w-2xl bg-white shadow-2xl flex flex-col">
                {/* Drawer Header */}
                <div className="p-5 border-b border-slate-200 bg-slate-50/80 flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-blue-600 text-white font-bold flex items-center justify-center text-sm shadow-xs">
                      {(activeEmployee.employee_name || 'E')
                        .split(' ')
                        .map(n => n[0])
                        .join('')
                        .toUpperCase()
                        .slice(0, 2)}
                    </div>
                    <div>
                      <h2 className="text-lg font-bold text-slate-900">
                        {activeEmployee.employee_name}
                      </h2>
                      <p className="text-xs text-slate-500">
                        ID: <span className="font-mono font-semibold text-slate-700">{activeEmployee.employee_code}</span>
                        {activeEmployee.department && ` · ${activeEmployee.department}`}
                        {activeEmployee.designation && ` · ${activeEmployee.designation}`}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setSelectedEmployeeId(null)}
                    className="p-1 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-200 transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                {/* Drawer Sub-Header Stats */}
                <div className="px-5 py-3 bg-slate-100/60 border-b border-slate-200 flex items-center gap-3 text-xs">
                  <span className="font-semibold text-slate-700">KYC Correction Requests:</span>
                  <span className="px-2 py-0.5 bg-white border border-slate-200 rounded font-medium text-slate-800">
                    {activeEmployee.summary.total} Total
                  </span>
                  <span className="px-2 py-0.5 bg-amber-50 border border-amber-200 text-amber-800 rounded font-medium">
                    {activeEmployee.summary.pending} Pending
                  </span>
                  <span className="px-2 py-0.5 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded font-medium">
                    {activeEmployee.summary.approved} Approved
                  </span>
                  <span className="px-2 py-0.5 bg-rose-50 border border-rose-200 text-rose-800 rounded font-medium">
                    {activeEmployee.summary.rejected} Rejected
                  </span>
                </div>

                {/* Drawer Content (Requests List) */}
                <div className="flex-1 overflow-y-auto p-5 space-y-4">
                  {activeEmployee.requests.map((req, idx) => (
                    <div
                      key={req.id}
                      className="bg-white border border-slate-200 rounded-xl p-4 space-y-3 shadow-xs"
                      data-testid={`kyc-req-item-${req.id}`}
                    >
                      {/* Field & Status Row */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="w-5 h-5 rounded-full bg-slate-100 text-slate-600 flex items-center justify-center font-bold text-[10px]">
                            {idx + 1}
                          </span>
                          <h3 className="font-bold text-slate-900 text-sm">
                            {req.field_label || req.field}
                          </h3>
                        </div>
                        <span
                          className={`px-2.5 py-0.5 rounded-full text-xs font-semibold border ${
                            STATUS_COLOR[req.hr_status] || 'bg-slate-100 text-slate-700'
                          }`}
                        >
                          {req.hr_status.toUpperCase()}
                        </span>
                      </div>

                      {/* Old -> New Value Diff */}
                      <div className="grid grid-cols-2 gap-3 p-3 bg-slate-50 rounded-lg border border-slate-100 text-xs">
                        <div>
                          <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 mb-0.5">
                            Old Value
                          </p>
                          <p className="text-slate-500 line-through font-medium break-all">
                            {(req.old_value ?? '—').toString() || '—'}
                          </p>
                        </div>
                        <div>
                          <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 mb-0.5">
                            New Value
                          </p>
                          <p className="text-emerald-700 font-bold break-all">
                            {(req.new_value ?? '—').toString() || '—'}
                          </p>
                        </div>
                      </div>

                      {/* Notes / Proof File */}
                      {(req.employee_note || req.proof_file_id) && (
                        <div className="text-xs space-y-1 pt-1">
                          {req.employee_note && (
                            <p className="text-slate-600 italic">
                              "{req.employee_note}"
                            </p>
                          )}
                          {req.proof_file_id && (
                            <a
                              href={`${API}/kyc/proofs/${req.proof_file_id}?preview=true`}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex items-center gap-1.5 text-blue-600 hover:text-blue-800 font-medium text-xs hover:underline"
                            >
                              <FileText className="w-3.5 h-3.5" /> View Proof Document
                            </a>
                          )}
                        </div>
                      )}

                      {/* Metadata & Reviewer Audit */}
                      <div className="flex items-center justify-between text-[11px] text-slate-400 pt-2 border-t border-slate-100">
                        <span>Cycle: {req.cycle_name || 'Standard'}</span>
                        <span>
                          Submitted: {req.created_at ? req.created_at.slice(0, 10) : '—'}
                        </span>
                      </div>

                      {/* Review Status Details / Action Bar */}
                      {req.hr_status === 'pending' ? (
                        <div className="flex gap-2 justify-end pt-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setDecisionFor({ item: req, action: 'reject', employeeName: activeEmployee.employee_name })}
                            className="border-rose-200 text-rose-700 hover:bg-rose-50 text-xs h-8"
                            data-testid={`kyc-corr-reject-${req.id}`}
                          >
                            <X className="w-3.5 h-3.5 mr-1" /> Reject Request
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => setDecisionFor({ item: req, action: 'approve', employeeName: activeEmployee.employee_name })}
                            className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs h-8"
                            data-testid={`kyc-corr-approve-${req.id}`}
                          >
                            <Check className="w-3.5 h-3.5 mr-1" /> Approve Request
                          </Button>
                        </div>
                      ) : (
                        <div className="p-2.5 rounded-lg bg-slate-50 border border-slate-100 text-xs text-slate-600 space-y-0.5">
                          <div className="flex items-center justify-between font-medium">
                            <span>Reviewed by: {req.reviewed_by_name || 'HR Admin'}</span>
                            <span>{req.reviewed_at ? req.reviewed_at.slice(0, 10) : ''}</span>
                          </div>
                          {req.hr_remarks && (
                            <p className="text-slate-500 italic">
                              Remarks: "{req.hr_remarks}"
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 5. Single Request Decision Modal */}
        {decisionFor && (
          <div
            className="fixed inset-0 bg-black/40 flex items-center justify-center z-60 p-4"
            onClick={() => setDecisionFor(null)}
          >
            <div
              className="bg-white rounded-xl p-5 w-full max-w-md shadow-xl space-y-4"
              onClick={e => e.stopPropagation()}
              data-testid="kyc-corr-decide-modal"
            >
              <div>
                <h3 className="text-lg font-bold text-slate-900">
                  {decisionFor.action === 'approve' ? 'Approve KYC Correction?' : 'Reject KYC Correction'}
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  Employee: <span className="font-semibold text-slate-800">{decisionFor.employeeName}</span>
                </p>
              </div>

              <div className="p-3 bg-slate-50 rounded-lg border border-slate-200 text-xs space-y-1">
                <p className="font-bold text-slate-800">
                  Field: {decisionFor.item.field_label || decisionFor.item.field}
                </p>
                <p className="text-slate-600">
                  Requested Change:{' '}
                  <span className="line-through text-slate-400">
                    {(decisionFor.item.old_value ?? '—').toString() || '—'}
                  </span>{' '}
                  → <span className="font-bold text-emerald-700">{(decisionFor.item.new_value ?? '—').toString() || '—'}</span>
                </p>
              </div>

              {decisionFor.action === 'approve' ? (
                <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-xs text-emerald-800 flex items-start gap-2">
                  <Check className="w-4 h-4 mt-0.5 shrink-0" />
                  <div>
                    Approving will patch the employee master profile for this field immediately and record an audit entry.
                  </div>
                </div>
              ) : (
                <div className="p-3 rounded-lg bg-rose-50 border border-rose-200 text-xs text-rose-800 flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                  <div>
                    Rejection requires a valid reason. The employee will be notified and can resubmit.
                  </div>
                </div>
              )}

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  HR Remarks {decisionFor.action === 'reject' && <span className="text-rose-600">*</span>}
                </label>
                <textarea
                  value={remarks}
                  onChange={e => setRemarks(e.target.value)}
                  rows={3}
                  placeholder={
                    decisionFor.action === 'approve'
                      ? 'Optional — e.g. "Verified proof document"'
                      : 'Required — explain why this correction request is rejected'
                  }
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-xs focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  data-testid="kyc-corr-remarks"
                />
              </div>

              <div className="flex gap-2 pt-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setDecisionFor(null)}
                  className="flex-1 text-xs"
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={decide}
                  disabled={submitting}
                  className={`flex-1 text-xs text-white ${
                    decisionFor.action === 'approve'
                      ? 'bg-emerald-600 hover:bg-emerald-700'
                      : 'bg-rose-600 hover:bg-rose-700'
                  }`}
                  data-testid="kyc-corr-confirm"
                >
                  {submitting ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : decisionFor.action === 'approve' ? (
                    'Approve Request'
                  ) : (
                    'Reject Request'
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
}
